<template>
  <div>
    <div class="network-list-view-table text-center">
      <ag-grid-vue
        class="ag-theme-alpine network-grid"
        style="width: 100%; height: auto;"
        :columnDefs="columnDefs"
        :rowData="networks"
        :defaultColDef="defaultColDef"
        :headerHeight="50"
        :rowHeight="60"
        domLayout="autoHeight"
        rowSelection="none"
        @grid-ready="onGridReady"
      ></ag-grid-vue>
    </div>
  </div>
</template>
<script>
import '@/lib/ag-grid';

export default {
  props: {
    networks: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      gridApi: null,
      columnApi: null,
      defaultColDef: {
        resizable: true,
        sortable: true,
        wrapText: true,
        autoHeight: true,
        flex: 1,
      },
      columnDefs: [
        {
          headerName: this.$t('network.name'),
          field: 'name',
          flex: 2,
          cellRenderer: params => {
            if (!params.value) return '';
            const slug = params.data.slug || '';
            const name = params.value || '';

            return `<div class="d-flex align-items-center">
              <span role="button" class="network-name text-primary fw-semibold" data-slug="${slug}" title="${name}">${name}</span>
            </div>`;
          },
          headerComponentParams: {
            template: 
              '<div class="ag-cell-label-container" role="presentation">' +
              '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
              '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
              '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
              '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
              '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
              '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
              '    <i class="fas fa-network-wired"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
              '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
              '  </div>' +
              '</div>'
          }
        },
        {
          headerName: this.$t('network.networkForm.purpose'),
          field: 'purpose',
          minWidth: 250,
          flex: 2,
          cellRenderer: params => {
            if (!params.value) return '';
            const purpose = params.value || '';
            const escapeHtml = (str) => {
                const div = document.createElement('div');
                div.textContent = str;
                return div.innerHTML;
            };
            const escapedPurpose = escapeHtml(purpose);
            const truncatedPurpose = purpose.length > 100 ? purpose.substring(0, 97) + '...' : purpose;
            const escapedTruncated = escapeHtml(truncatedPurpose);
            return `<span title="${escapedPurpose}">${escapedTruncated}</span>`;
          },
          headerComponentParams: {
            template: 
              '<div class="ag-cell-label-container" role="presentation">' +
              '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
              '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
              '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
              '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
              '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
              '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
              '    <i class="fas fa-bullseye-arrow"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
              '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
              '  </div>' +
              '</div>'
          }
        },
        {
          headerName: this.$t('network.tabMembers'),
          field: 'num_users',
          flex: 1,
          cellRenderer: params => {
            const count = params.value || 0;
            return `<span>${count}</span>`;
          },
          headerComponentParams: {
            template: 
              '<div class="ag-cell-label-container" role="presentation">' +
              '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
              '  <div ref="eLabel" class="ag-header-cell-label" role="presentation" style="justify-content: center;">' +
              '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
              '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
              '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
              '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
              '    <i class="fas fa-users"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
              '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
              '  </div>' +
              '</div>'
          }
        },
        {
          headerName: this.$t('nav.media'),
          field: 'media_count',
          flex: 1,
          cellRenderer: params => {
            const count = params.value || 0;
            return `<span>${count}</span>`;
          },
          headerComponentParams: {
            template: 
              '<div class="ag-cell-label-container" role="presentation">' +
              '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
              '  <div ref="eLabel" class="ag-header-cell-label" role="presentation" style="justify-content: center;">' +
              '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
              '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
              '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
              '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
              '    <i class="fas fa-photo-video"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
              '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
              '  </div>' +
              '</div>'
          }
        },
        {
          headerName: this.$t('access'),
          field: 'public',
          flex: 1,
          cellRenderer: params => {
            const isPublic = params.value;
            if (isPublic) {
              return '<span class="badge bg-primary p-2"><i class="fas fa-lock-open me-1"></i>' + this.$t('NetworkCard.public-access') + '</span>';
            } else {
              return '<span class="badge bg-info p-2"><i class="fas fa-lock me-1"></i>' + this.$t('NetworkCard.private-access') + '</span>';
            }
          },
          headerComponentParams: {
            template: 
              '<div class="ag-cell-label-container" role="presentation">' +
              '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
              '  <div ref="eLabel" class="ag-header-cell-label" role="presentation" style="justify-content: center;">' +
              '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
              '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
              '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
              '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
              '    <i class="fas fa-shield-alt"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
              '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
              '  </div>' +
              '</div>'
          }
        },
        {
          headerName: this.$t('your-role'),
          field: 'is_admin',
          flex: 1,
          cellRenderer: params => {
            const isAdmin = params.value;
            if (isAdmin) {
              return '<span class="badge bg-warning p-2"><i class="fas fa-crown me-1"></i>' + this.$t('network.member.admin') + '</span>';
            } else {
              return '<span class="badge bg-secondary p-2"><i class="fas fa-user me-1"></i>' + this.$t('member') + '</span>';
            }
          },
          headerComponentParams: {
            template: 
              '<div class="ag-cell-label-container" role="presentation">' +
              '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
              '  <div ref="eLabel" class="ag-header-cell-label" role="presentation" style="justify-content: center;">' +
              '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
              '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
              '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
              '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
              '    <i class="fas fa-user-shield"></i>&nbsp;<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
              '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
              '  </div>' +
              '</div>'
          }
        }
      ],
    };
  },
  beforeDestroy() {
    // remove resize event listener
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }

    // remove click event listener
    if (this._clickHandler && this.$el) {
      this.$el.removeEventListener('click', this._clickHandler);
      this._clickHandler = null;
    }
  },
  methods: {
    visitNetwork(slug) {
      this.$router.push({ name: 'network-home', params: { slug } });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;

      setTimeout(() => {
        this.gridApi.sizeColumnsToFit();
      }, 100);

      // store resize handler reference
      this._resizeHandler = () => {
        setTimeout(() => {
          if (this.gridApi) {
            this.gridApi.sizeColumnsToFit();
          }
        }, 100);
      };
      window.addEventListener('resize', this._resizeHandler);

      // store click handler reference and attach to component root
      this._clickHandler = (event) => {
        if (event.target.classList.contains('network-name')) {
          const slug = event.target.getAttribute('data-slug');
          if (slug) {
            this.visitNetwork(slug);
          }
        }
      };
      this.$el.addEventListener('click', this._clickHandler);
    }
  },
};
</script>
<style scoped>
.network-list-view-table {
    margin: 2rem 1rem 0 1rem !important;
}
.ag-theme-alpine {
  --ag-header-height: 50px;
  --ag-header-foreground-color: #5C6D80;
  --ag-header-background-color: #f8f9fa;
  --ag-header-cell-hover-background-color: #e9ecef;
  --ag-header-cell-moving-background-color: #e9ecef;
  --ag-row-hover-color: #e9ecef;
  --ag-font-size: 15px;
  --ag-font-family: inherit;
}
.ag-theme-alpine .ag-header-cell-label {
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  line-height: 30px;
  color: #5C6D80;
}
.ag-theme-alpine .ag-row {
  border-bottom-style: solid;
  border-bottom-width: 1px;
}
::v-deep .ag-cell {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 5px 10px !important;
  overflow: hidden;
  line-height: normal !important;
}
::v-deep .ag-cell[col-id="name"] {
  justify-content: flex-start !important;
  text-align: left !important;
}
::v-deep .ag-cell[col-id="purpose"] {
  justify-content: flex-start !important;
  text-align: left !important;
}
.network-grid {
  width: 100% !important;
  height: auto !important;
  min-height: 300px;
}
.network-name:hover {
  text-decoration: underline !important;
  cursor: pointer;
}
</style>